import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatGBP } from "@/lib/api";
import Logo from "@/components/Logo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, LogOut, Plus, Trash2 } from "lucide-react";

export default function AdminDashboard() {
  const nav = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("wa_admin_token") : null;

  useEffect(() => {
    if (!token) nav("/admin/login");
  }, [token, nav]);

  if (!token) return null;

  return (
    <div className="min-h-screen bg-black">
      <header className="bg-[#16171A] border-b border-[#2a2b2f] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-28 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Logo size={92} />
            <div className="hidden sm:block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gray-500">Admin console</div>
            </div>
          </Link>
          <button
            onClick={() => { localStorage.removeItem("wa_admin_token"); nav("/admin/login"); }}
            className="wa-btn-ghost text-sm"
            data-testid="admin-logout"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="bookings">
          <TabsList className="bg-[#0F0F10] border border-[#232428] mb-6" data-testid="admin-tabs">
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="availability" data-testid="admin-tab-availability">Availability</TabsTrigger>
            <TabsTrigger value="waste" data-testid="admin-tab-waste">Waste pricing</TabsTrigger>
            <TabsTrigger value="bulky" data-testid="admin-tab-bulky">Bulky items</TabsTrigger>
            <TabsTrigger value="skips" data-testid="admin-tab-skips">Skip comparison</TabsTrigger>
            <TabsTrigger value="charges" data-testid="admin-tab-charges">Other charges</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings"><Bookings /></TabsContent>
          <TabsContent value="availability"><Availability /></TabsContent>
          <TabsContent value="waste"><WasteTab /></TabsContent>
          <TabsContent value="bulky"><BulkyTab /></TabsContent>
          <TabsContent value="skips"><SkipsTab /></TabsContent>
          <TabsContent value="charges"><ChargesTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// -------------------- Bookings --------------------
function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [finalWeight, setFinalWeight] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings");
      setBookings(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const exportCSV = async () => {
    const url = `${api.defaults.baseURL}/admin/bookings/export`;
    const token = localStorage.getItem("wa_admin_token");
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "weighaway_bookings.csv";
    link.click();
  };

  const totals = bookings.reduce(
    (a, b) => ({
      total: a.total + (b.estimated_total || 0),
      profit: a.profit + (b.estimated_profit || 0),
      deposits: a.deposits + (b.deposit_paid ? b.deposit_due || 0 : 0),
      count: a.count + 1,
    }),
    { total: 0, profit: 0, deposits: 0, count: 0 }
  );

  const updateStatus = async (b, patch) => {
    const { data } = await api.patch(`/admin/bookings/${b.id}`, patch);
    setBookings((arr) => arr.map((x) => (x.id === b.id ? data : x)));
    if (selected?.id === b.id) setSelected(data);
    toast.success("Updated");
  };

  const submitFinalWeight = async () => {
    if (!selected) return;
    const w = parseFloat(finalWeight);
    if (isNaN(w) || w < 0) { toast.error("Enter a valid weight"); return; }
    await updateStatus(selected, { final_weight_kg: w });
    setFinalWeight("");
  };

  const cancelBooking = async (b) => {
    const reason = window.prompt("Reason for cancellation (sent to customer, optional):", "");
    if (reason === null) return; // cancel
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/cancel`, { reason, notify_customer: true });
      setBookings((arr) => arr.map((x) => (x.id === b.id ? data : x)));
      if (selected?.id === b.id) setSelected(data);
      toast.success("Booking cancelled · customer notified");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  };

  const deleteBooking = async (b) => {
    if (!window.confirm(`Permanently delete booking for ${b.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/bookings/${b.id}`);
      setBookings((arr) => arr.filter((x) => x.id !== b.id));
      if (selected?.id === b.id) setSelected(null);
      toast.success("Booking deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div>
      <div className="grid sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Bookings" value={totals.count} />
        <Stat label="Est. revenue" value={formatGBP(totals.total)} />
        <Stat label="Est. profit" value={formatGBP(totals.profit)} accent />
        <Stat label="Deposits paid" value={formatGBP(totals.deposits)} />
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-2xl font-bold text-white">All bookings</h2>
        <button onClick={exportCSV} className="wa-btn-ghost text-sm" data-testid="export-bookings">
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="overflow-x-auto wa-card p-0">
        <table className="w-full text-sm" data-testid="bookings-table">
          <thead className="bg-[#16171A] text-left text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="p-3">When</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Waste</th>
              <th className="p-3">Est. weight</th>
              <th className="p-3">Est. total</th>
              <th className="p-3">Est. profit</th>
              <th className="p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" className="p-6 text-center text-gray-500">Loading…</td></tr>}
            {!loading && bookings.length === 0 && (
              <tr><td colSpan="8" className="p-6 text-center text-gray-500">No bookings yet.</td></tr>
            )}
            {bookings.map((b) => (
              <tr key={b.id} className="border-t border-[#1a1a1c]" data-testid={`booking-row-${b.id}`}>
                <td className="p-3 whitespace-nowrap">
                  <div className="font-semibold text-white">{b.slot_date}</div>
                  <div className="text-xs text-gray-500">{b.slot_time}</div>
                </td>
                <td className="p-3">
                  <div className="font-semibold text-white">{b.name}</div>
                  <div className="text-xs text-gray-500">{b.email}</div>
                </td>
                <td className="p-3 text-gray-200">
                  <div>{b.waste_category_name}</div>
                  <div className="text-xs text-gray-500">{b.load_type === "we" ? "We-load" : "Self-load"}</div>
                </td>
                <td className="p-3 text-gray-200">{b.estimated_weight_kg} kg</td>
                <td className="p-3">
                  <div className="font-semibold text-white">{formatGBP(b.estimated_total)}</div>
                  <div className="text-xs text-gray-500">Cost {formatGBP(b.estimated_disposal_cost)}</div>
                </td>
                <td className="p-3 font-semibold text-[#8DC63F]">{formatGBP(b.estimated_profit)}</td>
                <td className="p-3">
                  <StatusBadge status={b.status} />
                  <div className="text-xs mt-1">
                    {b.deposit_paid ? <span className="text-[#8DC63F]">Deposit paid</span> : <span className="text-gray-500">Deposit due</span>}
                  </div>
                </td>
                <td className="p-3">
                  <button onClick={() => { setSelected(b); setFinalWeight(b.final_weight_kg ?? ""); }} className="wa-btn-ghost text-xs" data-testid={`open-booking-${b.id}`}>
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl" data-testid="booking-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{selected.name} · {selected.slot_date} {selected.slot_time}</DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2 text-sm">
                  <Row k="Email" v={selected.email} />
                  <Row k="Phone" v={selected.phone} />
                  <Row k="Address" v={`${selected.address}, ${selected.postcode}`} />
                  <Row k="Waste" v={`${selected.waste_category_name} · ${selected.estimated_weight_kg} kg`} />
                  <Row k="Load" v={selected.load_type === "we" ? "We-load" : "Self-load"} />
                  <Row k="Description" v={selected.description || "—"} />
                  {selected.bulky_items?.length > 0 && (
                    <Row k="Bulky" v={selected.bulky_items.map((i) => `${i.qty}×${i.item_id.slice(0, 6)}`).join(", ")} />
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <Row k="Estimated total" v={formatGBP(selected.estimated_total)} />
                  <Row k="Estimated disposal cost" v={formatGBP(selected.estimated_disposal_cost)} />
                  <Row k="Estimated profit" v={formatGBP(selected.estimated_profit)} />
                  <Row k="Deposit due / paid" v={`${formatGBP(selected.deposit_due)} ${selected.deposit_paid ? "✓" : ""}`} />
                  <Row k="Balance due" v={`${formatGBP(selected.balance_due)} ${selected.balance_paid ? "✓" : ""}`} />
                  {selected.final_weight_kg != null && (
                    <>
                      <div className="border-t border-dashed pt-2 mt-2" />
                      <Row k="Final weight" v={`${selected.final_weight_kg} kg`} />
                      <Row k="Final total" v={formatGBP(selected.final_total)} />
                      <Row k="Final profit" v={formatGBP(selected.final_profit)} />
                    </>
                  )}
                </div>
              </div>

              {selected.photos?.length > 0 && (
                <div className="mt-3">
                  <div className="wa-label">Photos</div>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {selected.photos.map((p, i) => (
                      <a href={p} target="_blank" rel="noreferrer" key={i} className="aspect-square rounded-lg overflow-hidden border border-gray-200 block">
                        <img src={p} alt={`photo ${i}`} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="wa-label">Update status</div>
                  <select
                    className="wa-input"
                    value={selected.status}
                    onChange={(e) => updateStatus(selected, { status: e.target.value })}
                    data-testid="booking-status-select"
                  >
                    {["pending_payment", "confirmed", "in_progress", "completed", "cancelled"].map((s) => (
                      <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="wa-label">Set final weight (kg)</div>
                  <div className="flex gap-2">
                    <input type="number" className="wa-input" value={finalWeight} onChange={(e) => setFinalWeight(e.target.value)} data-testid="booking-final-weight" />
                    <button onClick={submitFinalWeight} className="wa-btn-primary">Save</button>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <div className="flex flex-wrap gap-2 w-full">
                  <button onClick={() => updateStatus(selected, { deposit_paid: !selected.deposit_paid })} className="wa-btn-outline text-sm" data-testid="toggle-deposit-paid">
                    Mark deposit {selected.deposit_paid ? "unpaid" : "paid"}
                  </button>
                  <button onClick={() => updateStatus(selected, { balance_paid: !selected.balance_paid })} className="wa-btn-outline text-sm" data-testid="toggle-balance-paid">
                    Mark balance {selected.balance_paid ? "unpaid" : "paid"}
                  </button>
                  <div className="ml-auto flex gap-2">
                    {selected.status !== "cancelled" && (
                      <button
                        onClick={() => cancelBooking(selected)}
                        className="text-xs font-semibold px-4 py-2.5 rounded-full border-2 border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition"
                        data-testid="cancel-booking"
                      >
                        Cancel booking
                      </button>
                    )}
                    <button
                      onClick={() => deleteBooking(selected)}
                      className="text-xs font-semibold px-4 py-2.5 rounded-full border-2 border-red-500/40 text-red-300 hover:bg-red-500/10 transition"
                      data-testid="delete-booking"
                    >
                      <Trash2 size={12} className="inline mr-1" /> Delete
                    </button>
                  </div>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending_payment: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
    confirmed: "bg-[#8DC63F]/15 text-[#8DC63F] border border-[#8DC63F]/30",
    in_progress: "bg-blue-500/15 text-blue-300 border border-blue-500/30",
    completed: "bg-white text-black",
    cancelled: "bg-red-500/15 text-red-300 border border-red-500/30",
  };
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${map[status] || "bg-white/10 text-white"}`}>{status?.replace(/_/g, " ")}</span>;
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-gray-500">{k}</span>
      <span className="font-semibold text-right text-white">{v}</span>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`wa-card ${accent ? "border-[#8DC63F] border-2" : ""}`}>
      <div className="text-xs uppercase tracking-[0.18em] text-gray-500">{label}</div>
      <div className="font-display text-2xl font-black mt-1 text-white">{value}</div>
    </div>
  );
}

// -------------------- Availability --------------------
function Availability() {
  const [slots, setSlots] = useState([]);
  const [tab, setTab] = useState("recurring"); // recurring | single
  const [form, setForm] = useState({ date: "", start_time: "09:00", end_time: "12:00", label: "" });

  // recurring form state
  const today = new Date();
  const fourWeeks = new Date(Date.now() + 28 * 24 * 60 * 60 * 1000);
  const toISO = (d) => d.toISOString().slice(0, 10);
  const [bulk, setBulk] = useState({
    start_date: toISO(today),
    end_date: toISO(fourWeeks),
    days_of_week: [0, 1, 2, 3, 4], // Mon–Fri
    time_windows: [{ start_time: "09:00", end_time: "12:00", label: "" }],
  });
  const [submittingBulk, setSubmittingBulk] = useState(false);

  const refresh = async () => {
    const { data } = await api.get("/availability?only_open=false");
    setSlots(data);
  };
  useEffect(() => { refresh(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error("Pick a date"); return; }
    await api.post("/admin/availability", { ...form });
    setForm({ ...form, label: "" });
    refresh();
    toast.success("Slot added");
  };

  const del = async (id) => {
    await api.delete(`/admin/availability/${id}`);
    refresh();
  };

  const toggleDay = (d) =>
    setBulk((b) => ({
      ...b,
      days_of_week: b.days_of_week.includes(d) ? b.days_of_week.filter((x) => x !== d) : [...b.days_of_week, d].sort(),
    }));

  const updateWindow = (i, k, v) =>
    setBulk((b) => ({
      ...b,
      time_windows: b.time_windows.map((w, idx) => (idx === i ? { ...w, [k]: v } : w)),
    }));
  const addWindow = () =>
    setBulk((b) => ({ ...b, time_windows: [...b.time_windows, { start_time: "13:00", end_time: "16:00", label: "" }] }));
  const removeWindow = (i) =>
    setBulk((b) => ({ ...b, time_windows: b.time_windows.filter((_, idx) => idx !== i) }));

  const submitBulk = async (e) => {
    e.preventDefault();
    if (!bulk.start_date || !bulk.end_date) { toast.error("Pick a date range"); return; }
    if (!bulk.days_of_week.length) { toast.error("Select at least one day"); return; }
    if (!bulk.time_windows.length) { toast.error("Add at least one time window"); return; }
    setSubmittingBulk(true);
    try {
      const { data } = await api.post("/admin/availability/bulk", bulk);
      toast.success(`Created ${data.created} slots${data.skipped ? ` (${data.skipped} duplicates skipped)` : ""}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create slots");
    } finally {
      setSubmittingBulk(false);
    }
  };

  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5 space-y-3">
        <div className="flex gap-2 bg-[#0F0F10] border border-[#232428] rounded-full p-1 w-fit" data-testid="availability-mode">
          <button
            onClick={() => setTab("recurring")}
            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition ${tab === "recurring" ? "bg-[#8DC63F] text-black" : "text-gray-300 hover:text-white"}`}
            data-testid="availability-tab-recurring"
          >
            Recurring slots
          </button>
          <button
            onClick={() => setTab("single")}
            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition ${tab === "single" ? "bg-[#8DC63F] text-black" : "text-gray-300 hover:text-white"}`}
            data-testid="availability-tab-single"
          >
            Single slot
          </button>
        </div>

        {tab === "recurring" && (
          <form onSubmit={submitBulk} className="wa-card" data-testid="recurring-slots-form">
            <h3 className="font-display text-xl font-bold mb-1 text-white">Recurring slots</h3>
            <p className="text-xs text-gray-400 mb-3">Pick days of the week and one or more time windows to apply across a date range.</p>

            <div className="grid grid-cols-2 gap-2">
              <Field label="Start date">
                <input type="date" className="wa-input" value={bulk.start_date} onChange={(e) => setBulk({ ...bulk, start_date: e.target.value })} data-testid="bulk-start-date" />
              </Field>
              <Field label="End date">
                <input type="date" className="wa-input" value={bulk.end_date} onChange={(e) => setBulk({ ...bulk, end_date: e.target.value })} data-testid="bulk-end-date" />
              </Field>
            </div>

            <div className="mt-3">
              <label className="wa-label">Days of the week</label>
              <div className="flex flex-wrap gap-1.5" data-testid="bulk-days">
                {DAYS.map((d, idx) => {
                  const active = bulk.days_of_week.includes(idx);
                  return (
                    <button
                      type="button"
                      key={d}
                      onClick={() => toggleDay(idx)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${active ? "bg-[#8DC63F] text-black border-[#8DC63F]" : "bg-[#0a0a0a] text-gray-300 border-[#232428] hover:text-white"}`}
                      data-testid={`bulk-day-${d.toLowerCase()}`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <label className="wa-label !mb-0">Time windows</label>
                <button type="button" onClick={addWindow} className="text-xs text-[#8DC63F] font-semibold" data-testid="bulk-add-window">+ Add window</button>
              </div>
              <div className="space-y-2">
                {bulk.time_windows.map((w, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`bulk-window-${i}`}>
                    <input type="time" className="wa-input col-span-3" value={w.start_time} onChange={(e) => updateWindow(i, "start_time", e.target.value)} />
                    <span className="col-span-1 text-center text-gray-500">–</span>
                    <input type="time" className="wa-input col-span-3" value={w.end_time} onChange={(e) => updateWindow(i, "end_time", e.target.value)} />
                    <input className="wa-input col-span-4" placeholder="Label (optional)" value={w.label} onChange={(e) => updateWindow(i, "label", e.target.value)} />
                    <button type="button" onClick={() => removeWindow(i)} className="col-span-1 text-red-400" disabled={bulk.time_windows.length === 1} data-testid={`bulk-window-remove-${i}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <button disabled={submittingBulk} className="wa-btn-primary w-full justify-center mt-4 disabled:opacity-50" data-testid="bulk-submit">
              <Plus size={16} /> {submittingBulk ? "Creating…" : "Create recurring slots"}
            </button>
          </form>
        )}

        {tab === "single" && (
          <form onSubmit={add} className="wa-card" data-testid="add-slot-form">
            <h3 className="font-display text-xl font-bold mb-3 text-white">Add a single slot</h3>
            <div className="space-y-3">
              <Field label="Date"><input type="date" className="wa-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="slot-date" /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Start"><input type="time" className="wa-input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="slot-start" /></Field>
                <Field label="End"><input type="time" className="wa-input" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="slot-end" /></Field>
              </div>
              <Field label="Label (optional)"><input className="wa-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Morning · van 2" /></Field>
              <button className="wa-btn-primary w-full justify-center" data-testid="add-slot-submit"><Plus size={16} /> Add slot</button>
            </div>
          </form>
        )}
      </div>

      <div className="lg:col-span-7 wa-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#16171A] text-left text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="p-3">Date</th>
              <th className="p-3">Time</th>
              <th className="p-3">Label</th>
              <th className="p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {slots.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-gray-500">No slots yet.</td></tr>}
            {slots.map((s) => (
              <tr key={s.id} className="border-t border-[#1a1a1c]">
                <td className="p-3 font-semibold text-white">{s.date}</td>
                <td className="p-3 text-gray-200">{s.start_time}–{s.end_time}</td>
                <td className="p-3 text-gray-300">{s.label || "—"}</td>
                <td className="p-3">{s.booked ? <span className="wa-chip">Booked</span> : <span className="wa-chip wa-chip-green">Open</span>}</td>
                <td className="p-3">
                  {!s.booked && (
                    <button onClick={() => del(s.id)} className="text-red-400 text-xs flex items-center gap-1" data-testid={`delete-slot-${s.id}`}>
                      <Trash2 size={14} /> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="wa-label">{label}</label>
      {children}
    </div>
  );
}

// -------------------- Waste pricing --------------------
function WasteTab() {
  const [items, setItems] = useState([]);
  const refresh = async () => {
    const { data } = await api.get("/pricing/waste-categories?include_inactive=true");
    setItems(data);
  };
  useEffect(() => { refresh(); }, []);

  const save = async (item) => {
    await api.post("/admin/waste-categories", item);
    toast.success("Saved");
    refresh();
  };
  const del = async (id) => { await api.delete(`/admin/waste-categories/${id}`); refresh(); };
  const addNew = () => setItems((arr) => [{ id: crypto.randomUUID(), name: "New waste type", cost_per_kg: 0.1, sale_per_kg: 0.3, vat_included: true, active: true, _new: true }, ...arr]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-2xl font-bold text-white">Waste categories</h2>
        <button onClick={addNew} className="wa-btn-primary text-sm" data-testid="add-waste">
          <Plus size={14} /> Add category
        </button>
      </div>
      <div className="overflow-x-auto wa-card p-0">
        <table className="w-full text-sm">
          <thead className="bg-[#16171A] text-left text-xs uppercase tracking-wider text-gray-500">
            <tr><th className="p-3">Name</th><th className="p-3">Cost / kg</th><th className="p-3">Sale / kg</th><th className="p-3">VAT</th><th className="p-3">Active</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((it, idx) => (
              <tr key={it.id} className="border-t border-[#1a1a1c]">
                <td className="p-2"><input className="wa-input" value={it.name} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} /></td>
                <td className="p-2"><input type="number" step="0.01" className="wa-input w-24" value={it.cost_per_kg} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, cost_per_kg: parseFloat(e.target.value || 0) } : x))} /></td>
                <td className="p-2"><input type="number" step="0.01" className="wa-input w-24" value={it.sale_per_kg} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, sale_per_kg: parseFloat(e.target.value || 0) } : x))} /></td>
                <td className="p-2"><input type="checkbox" checked={it.vat_included} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, vat_included: e.target.checked } : x))} /></td>
                <td className="p-2"><input type="checkbox" checked={it.active} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, active: e.target.checked } : x))} /></td>
                <td className="p-2 flex gap-2">
                  <button onClick={() => save(it)} className="wa-btn-primary text-xs" data-testid={`save-waste-${it.id}`}>Save</button>
                  <button onClick={() => del(it.id)} className="text-red-400 text-xs"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------- Bulky items --------------------
function BulkyTab() {
  const [items, setItems] = useState([]);
  const refresh = async () => {
    const { data } = await api.get("/pricing/bulky-items?include_inactive=true");
    setItems(data);
  };
  useEffect(() => { refresh(); }, []);
  const save = async (it) => { await api.post("/admin/bulky-items", it); toast.success("Saved"); refresh(); };
  const del = async (id) => { await api.delete(`/admin/bulky-items/${id}`); refresh(); };
  const addNew = () => setItems((arr) => [{ id: crypto.randomUUID(), name: "New item", cost: 0, sale_price: 10, notes: "", active: true }, ...arr]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-2xl font-bold text-white">Bulky items</h2>
        <button onClick={addNew} className="wa-btn-primary text-sm" data-testid="add-bulky"><Plus size={14} /> Add item</button>
      </div>
      <div className="overflow-x-auto wa-card p-0">
        <table className="w-full text-sm">
          <thead className="bg-[#16171A] text-left text-xs uppercase tracking-wider text-gray-500">
            <tr><th className="p-3">Name</th><th className="p-3">Cost</th><th className="p-3">Sale</th><th className="p-3">Notes</th><th className="p-3">Active</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((it, idx) => (
              <tr key={it.id} className="border-t border-[#1a1a1c]">
                <td className="p-2"><input className="wa-input" value={it.name} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} /></td>
                <td className="p-2"><input type="number" step="0.01" className="wa-input w-24" value={it.cost} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, cost: parseFloat(e.target.value || 0) } : x))} /></td>
                <td className="p-2"><input type="number" step="0.01" className="wa-input w-24" value={it.sale_price} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, sale_price: parseFloat(e.target.value || 0) } : x))} /></td>
                <td className="p-2"><input className="wa-input" value={it.notes || ""} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, notes: e.target.value } : x))} /></td>
                <td className="p-2"><input type="checkbox" checked={it.active} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, active: e.target.checked } : x))} /></td>
                <td className="p-2 flex gap-2">
                  <button onClick={() => save(it)} className="wa-btn-primary text-xs">Save</button>
                  <button onClick={() => del(it.id)} className="text-red-400 text-xs"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------- Skips --------------------
function SkipsTab() {
  const [items, setItems] = useState([]);
  const refresh = async () => {
    const { data } = await api.get("/pricing/skip-sizes?include_inactive=true");
    setItems(data);
  };
  useEffect(() => { refresh(); }, []);
  const save = async (it) => { await api.post("/admin/skip-sizes", it); toast.success("Saved"); refresh(); };
  const del = async (id) => { await api.delete(`/admin/skip-sizes/${id}`); refresh(); };
  const addNew = () => setItems((arr) => [...arr, { id: crypto.randomUUID(), name: "New skip", weight_min_kg: 0, weight_max_kg: 250, price_low: 100, price_high: 150, notes: "", active: true }]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-2xl font-bold text-white">Skip comparison</h2>
        <button onClick={addNew} className="wa-btn-primary text-sm" data-testid="add-skip"><Plus size={14} /> Add skip</button>
      </div>
      <div className="overflow-x-auto wa-card p-0">
        <table className="w-full text-sm">
          <thead className="bg-[#16171A] text-left text-xs uppercase tracking-wider text-gray-500">
            <tr><th className="p-3">Name</th><th className="p-3">Weight min</th><th className="p-3">Weight max</th><th className="p-3">Price low</th><th className="p-3">Price high</th><th className="p-3">Notes</th><th className="p-3">Active</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((it, idx) => (
              <tr key={it.id} className="border-t border-[#1a1a1c]">
                <td className="p-2"><input className="wa-input" value={it.name} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} /></td>
                <td className="p-2"><input type="number" className="wa-input w-24" value={it.weight_min_kg} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, weight_min_kg: parseInt(e.target.value || 0) } : x))} /></td>
                <td className="p-2"><input type="number" className="wa-input w-24" value={it.weight_max_kg} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, weight_max_kg: parseInt(e.target.value || 0) } : x))} /></td>
                <td className="p-2"><input type="number" step="0.01" className="wa-input w-24" value={it.price_low} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, price_low: parseFloat(e.target.value || 0) } : x))} /></td>
                <td className="p-2"><input type="number" step="0.01" className="wa-input w-24" value={it.price_high} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, price_high: parseFloat(e.target.value || 0) } : x))} /></td>
                <td className="p-2"><input className="wa-input" value={it.notes || ""} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, notes: e.target.value } : x))} /></td>
                <td className="p-2"><input type="checkbox" checked={it.active} onChange={(e) => setItems((a) => a.map((x, i) => i === idx ? { ...x, active: e.target.checked } : x))} /></td>
                <td className="p-2 flex gap-2">
                  <button onClick={() => save(it)} className="wa-btn-primary text-xs">Save</button>
                  <button onClick={() => del(it.id)} className="text-red-400 text-xs"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------- Other charges --------------------
function ChargesTab() {
  const [c, setC] = useState(null);
  const [resetting, setResetting] = useState(false);
  const refresh = () => api.get("/pricing/other-charges").then((r) => setC(r.data));
  useEffect(() => { refresh(); }, []);
  const save = async () => { await api.put("/admin/other-charges", c); toast.success("Saved"); };
  const resetDefaults = async () => {
    if (!window.confirm("Reset ALL waste categories, skip prices and service charges to the factory defaults? This wipes any custom prices you've set.")) return;
    setResetting(true);
    try {
      await api.post("/admin/reset-pricing-defaults");
      toast.success("Pricing reset to defaults");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset");
    } finally {
      setResetting(false);
    }
  };
  if (!c) return null;
  return (
    <div className="space-y-6 max-w-2xl" data-testid="charges-card">
      <div className="wa-card">
        <h2 className="font-display text-2xl font-bold mb-4 text-white">Service charges</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <NumField label="Self-load minimum (£)" v={c.self_load_min_charge} on={(v) => setC({ ...c, self_load_min_charge: v })} />
          <NumField label="We-load minimum (£)" v={c.we_load_min_charge} on={(v) => setC({ ...c, we_load_min_charge: v })} />
          <NumField label="We-load surcharge (% on weight charge)" v={c.we_load_surcharge_percent} on={(v) => setC({ ...c, we_load_surcharge_percent: v })} />
          <NumField label="We-load extra loading fee (£)" v={c.we_load_fee} on={(v) => setC({ ...c, we_load_fee: v })} />
          <NumField label="Deposit percent (%)" v={c.deposit_percent} on={(v) => setC({ ...c, deposit_percent: v })} />
          <NumField label="Deposit minimum (£)" v={c.deposit_min} on={(v) => setC({ ...c, deposit_min: v })} />
          <NumField label="Mileage £/mile" v={c.mileage_charge_per_mile} on={(v) => setC({ ...c, mileage_charge_per_mile: v })} />
        </div>
        <Field label="Standard area postcodes (comma separated prefixes)">
          <input className="wa-input" value={c.standard_area_postcodes || ""} onChange={(e) => setC({ ...c, standard_area_postcodes: e.target.value })} data-testid="charges-postcodes" />
        </Field>
        <button onClick={save} className="wa-btn-primary mt-4" data-testid="charges-save">Save changes</button>
      </div>

      <div className="wa-card border-red-500/30">
        <h3 className="font-display text-lg font-bold text-white">Reset pricing</h3>
        <p className="text-sm text-gray-400 mt-1">
          Wipes waste categories, skip prices, bulky items and service charges and re-applies the canonical defaults.
        </p>
        <button onClick={resetDefaults} disabled={resetting} className="mt-3 text-xs font-semibold px-4 py-2.5 rounded-full border-2 border-red-500/40 text-red-300 hover:bg-red-500/10 transition disabled:opacity-50" data-testid="reset-pricing-defaults">
          {resetting ? "Resetting…" : "Reset to factory defaults"}
        </button>
      </div>
    </div>
  );
}

function NumField({ label, v, on }) {
  return (
    <div>
      <label className="wa-label">{label}</label>
      <input type="number" step="0.01" className="wa-input" value={v} onChange={(e) => on(parseFloat(e.target.value || 0))} />
    </div>
  );
}
